<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { publicAppOrigin } from '#lib/publicUrls.ts';
</script>

<svelte:head>
  <title>{pageTitle('Privacy Policy')}</title>
</svelte:head>

<div class="container clear-nav">
  <h1>Privacy Policy</h1>
  <p><strong>Last updated:</strong> May 7, 2026</p>
  <p>
    This Privacy Policy explains how SpinFlip LLC ("we", "us", "our") collects,
    uses, shares, and protects your personal information when you visit
    <a href={publicAppOrigin()} target="_blank" rel="noopener noreferrer"
      >{publicAppOrigin()}</a
    > (the "Service"). It also describes your privacy rights under applicable
    laws, including the EU and UK General Data Protection Regulation (GDPR /
    UK GDPR), the California Consumer Privacy Act as amended by the California
    Privacy Rights Act (CCPA/CPRA), and similar U.S. state privacy laws.
  </p>
  <p>
    For information specifically about cookies and similar technologies, please
    see our <a href="/cookies">Cookie Policy</a>. You can change your cookie
    choices at any time using the "Cookie Preferences" link in the site
    footer. Our <a href="/terms">Terms of Service</a> govern your use of the
    Service, including online purchases.
  </p>

  <h2>1. Key Definitions</h2>
  <ul>
    <li>
      <strong>Account:</strong> A unique account created for you to access
      parts of our Service.
    </li>
    <li>
      <strong>Company:</strong> SpinFlip LLC, Columbus, OH 43214, United
      States.
    </li>
    <li>
      <strong>Cookies and similar technologies:</strong> Small data files or
      identifiers stored on or read from your device, including HTTP cookies,
      <code>localStorage</code>, and <code>sessionStorage</code>.
    </li>
    <li>
      <strong>Personal Data / Personal Information:</strong> Any information
      that identifies, relates to, describes, or could reasonably be linked to
      an identified or identifiable natural person.
    </li>
    <li>
      <strong>Service Provider:</strong> A third party that processes data on
      our behalf to help us provide the Service (for example, hosting,
      analytics, payment processing).
    </li>
    <li>
      <strong>Usage Data:</strong> Data automatically collected when you use
      the Service, such as IP address, browser type, and pages visited.
    </li>
    <li>
      <strong>You:</strong> The natural person using the Service.
    </li>
  </ul>

  <h2>2. Categories of Personal Information We Collect</h2>
  <p>
    Within the past 12 months we have collected, or may collect, the following
    categories of personal information (CCPA/CPRA category labels in
    parentheses):
  </p>
  <ul>
    <li>
      <strong>Identifiers (Cal. Civ. Code §1798.140(v)(1)(A)):</strong>
      name, email address, phone number, IP address, and unique device or
      browser identifiers.
    </li>
    <li>
      <strong>Customer records information (B):</strong> contact and order
      information you provide when you make a purchase or send us a message.
    </li>
    <li>
      <strong>Commercial information (D):</strong> products viewed, items added
      to your cart, and purchase history.
    </li>
    <li>
      <strong>Internet or network activity (F):</strong> pages visited, time
      and duration of visits, referring URL, and interactions with the
      Service.
    </li>
    <li>
      <strong>Geolocation data (G):</strong> approximate location inferred
      from your IP address.
    </li>
    <li>
      <strong>Inferences (K):</strong> aggregated insights about how visitors
      use the site, derived from analytics.
    </li>
  </ul>
  <p>
    We do <strong>not</strong> knowingly collect "sensitive personal
    information" (e.g., government IDs, precise geolocation, health, biometric
    data, racial or ethnic origin). We do not collect information from
    children under 13 (see Section 10).
  </p>

  <h2>3. Sources of Personal Information</h2>
  <ul>
    <li>Directly from you (e.g., contact form, account creation, checkout).</li>
    <li>
      Automatically from your device when you visit the Service (e.g., IP
      address, browser type, cookie identifiers).
    </li>
    <li>
      From service providers that help us operate the Service (e.g., Stripe
      for payments, Google for analytics).
    </li>
  </ul>

  <h2>4. How We Use Your Information</h2>
  <ul>
    <li>To provide and maintain the Service, including your shopping cart and account.</li>
    <li>To process and fulfill orders and respond to inquiries.</li>
    <li>To improve the Service, troubleshoot issues, and ensure security.</li>
    <li>To analyze how visitors use the Service (only with your consent).</li>
    <li>To comply with legal obligations.</li>
  </ul>

  <h3>Lawful bases under GDPR / UK GDPR (Article 6)</h3>
  <ul>
    <li>
      <strong>Performance of a contract</strong> (Art. 6(1)(b)) — to maintain
      your shopping cart, account, and fulfill orders.
    </li>
    <li>
      <strong>Legitimate interests</strong> (Art. 6(1)(f)) — to keep the
      Service secure, prevent fraud, and respond to your inquiries.
    </li>
    <li>
      <strong>Consent</strong> (Art. 6(1)(a)) — for non-essential cookies and
      analytics. You may withdraw consent at any time via "Cookie Preferences"
      in the footer.
    </li>
    <li>
      <strong>Legal obligation</strong> (Art. 6(1)(c)) — to comply with tax,
      accounting, and other applicable laws.
    </li>
  </ul>

  <h2>5. Cookies and Similar Technologies</h2>
  <p>
    We use cookies and browser storage to operate the Service and, with your
    consent, to measure how the Service is used. A full list of cookies and
    storage items, including provider, purpose, and lifetime, is available in
    our <a href="/cookies">Cookie Policy</a>.
  </p>
  <p>
    On your first visit you will see a cookie banner that allows you to
    <strong>Accept all</strong>, <strong>Reject all</strong>, or
    <strong>Manage preferences</strong>. Strictly necessary cookies cannot be
    disabled because the Service cannot function without them. We honor the
    <a href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer">Global Privacy Control (GPC)</a>
    browser signal: when GPC is enabled we automatically opt you out of
    non-essential cookies and treat the signal as a request to opt out of any
    "sale" or "sharing" of personal information under U.S. state privacy laws.
  </p>

  <h2>6. How We Share Your Information</h2>
  <p>
    We do <strong>not</strong> sell your personal information for money. We
    may disclose personal information to the following categories of
    recipients:
  </p>
  <ul>
    <li>
      <strong>Google LLC</strong> — Google Analytics 4. Receives pseudonymous
      usage data (e.g., page views, IP-derived approximate location, device
      and browser characteristics) only when you have granted analytics
      consent. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
    </li>
    <li>
      <strong>Stripe, Inc.</strong> — Payment processor. When you check out,
      payment details are collected directly by Stripe; we do not store full
      card numbers. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
    </li>
    <li>
      <strong>Content delivery and font providers</strong> — jsDelivr (Bootstrap),
      Cloudflare CDN (Font Awesome), and Google Fonts. These services receive
      your IP address and browser headers when your browser requests assets.
    </li>
    <li>
      <strong>Hosting and infrastructure providers</strong> — used to host the
      Service and process database transactions on our behalf.
    </li>
    <li>
      <strong>Legal authorities</strong> — when required to comply with a
      lawful request, court order, or to protect our rights, property, or
      safety, or that of others.
    </li>
    <li>
      <strong>In a business transfer</strong> — if SpinFlip LLC is involved in
      a merger, acquisition, or sale of assets, your information may be
      transferred as part of that transaction.
    </li>
  </ul>

  <h2>7. International Data Transfers</h2>
  <p>
    SpinFlip LLC is based in the United States and stores most data in the
    United States. Some of our service providers (notably Google and Stripe)
    may process your information in the United States or other countries. Where
    we transfer personal data of individuals in the European Economic Area,
    United Kingdom, or Switzerland to a country that has not received an
    adequacy decision, we rely on the European Commission's
    <a href="https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en" target="_blank" rel="noopener noreferrer">Standard Contractual Clauses</a>
    and equivalent UK and Swiss safeguards through our service providers'
    contractual commitments.
  </p>

  <h2>8. Data Retention</h2>
  <ul>
    <li>
      <strong>Account and order records</strong> — kept for as long as your
      account is active and for up to 7 years after the last transaction to
      meet tax and accounting obligations.
    </li>
    <li>
      <strong>Contact form messages</strong> — kept for up to 24 months from
      our last interaction with you.
    </li>
    <li>
      <strong>Analytics data</strong> — Google Analytics 4 user-level and
      event-level data is retained for the minimum supported period (currently
      2 months); aggregated reports may be kept indefinitely.
    </li>
    <li>
      <strong>Cookie consent record</strong> — your cookie choice is stored in
      your browser for 12 months, after which we will ask again.
    </li>
  </ul>

  <h2>9. Your Privacy Rights</h2>

  <h3>EU / UK / Swiss residents (GDPR / UK GDPR)</h3>
  <p>You have the right to:</p>
  <ul>
    <li>Access the personal data we hold about you.</li>
    <li>Request correction of inaccurate data.</li>
    <li>Request erasure ("right to be forgotten") in certain circumstances.</li>
    <li>Restrict or object to processing.</li>
    <li>Data portability for data you provided to us.</li>
    <li>Withdraw consent at any time without affecting prior lawful processing.</li>
    <li>
      Lodge a complaint with your local supervisory authority (e.g., your
      country's Data Protection Authority or, in the UK, the
      <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer">Information Commissioner's Office</a>).
    </li>
  </ul>

  <h3>California, Colorado, Connecticut, Virginia, and other U.S. state residents</h3>
  <p>Subject to verification, you have the right to:</p>
  <ul>
    <li>Know what personal information we collect, use, and disclose.</li>
    <li>Request access to a copy of your personal information.</li>
    <li>Request deletion of your personal information.</li>
    <li>Request correction of inaccurate personal information.</li>
    <li>Opt out of the "sale" or "sharing" of your personal information.</li>
    <li>Limit the use and disclosure of "sensitive personal information."</li>
    <li>
      Not receive discriminatory treatment for exercising your rights
      (Cal. Civ. Code §1798.125).
    </li>
  </ul>
  <p>
    You may exercise these rights by emailing
    <a href="mailto:support@spin-flip.com">support@spin-flip.com</a>. We will
    verify your identity using information you provide, and we will respond
    within the timeframes required by applicable law (generally 45 days under
    CCPA/CPRA, extendable once for an additional 45 days; one month under
    GDPR, extendable by two further months for complex requests).
  </p>
  <p>
    You may designate an authorized agent to make a request on your behalf.
    Agents must provide written authorization, and we may still ask you to
    verify your identity directly.
  </p>

  <h3 id="do-not-sell-or-share">Do Not Sell or Share My Personal Information</h3>
  <p>
    We do not sell personal information for monetary value. However, when
    Google Analytics is enabled with your consent, the disclosure of online
    identifiers and usage data to Google may qualify as "sharing" for
    cross-context behavioral advertising purposes under the CCPA/CPRA.
  </p>
  <p>
    To opt out of any such "sharing":
  </p>
  <ul>
    <li>
      Use the <strong>Cookie Preferences</strong> link in the site footer and
      reject the Analytics category, or
    </li>
    <li>
      Enable the
      <a href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer">Global Privacy Control (GPC)</a>
      signal in your browser — we honor it automatically, or
    </li>
    <li>
      Email <a href="mailto:support@spin-flip.com">support@spin-flip.com</a>
      with the subject line "Do Not Sell or Share."
    </li>
  </ul>

  <h2>10. Children's Privacy</h2>
  <p>
    The Service is not directed to children under 13, and we do not knowingly
    collect personal information from children under 13 (or under 16 in the
    EU/UK without parental consent). If you believe a child has provided us
    with personal information, please contact us at
    <a href="mailto:support@spin-flip.com">support@spin-flip.com</a> and we
    will delete it.
  </p>

  <h2>11. Data Security</h2>
  <p>
    We use appropriate technical and organizational measures, including
    HTTPS/TLS in transit and access controls at rest, to protect your personal
    information. No method of transmission or storage is 100% secure, and we
    cannot guarantee absolute security.
  </p>

  <h2>12. Changes to This Policy</h2>
  <p>
    We may update this Privacy Policy from time to time. When we do, we will
    update the "Last updated" date at the top of this page. If changes are
    material, we will provide additional notice (for example, by re-prompting
    you for cookie consent or posting a banner on the site).
  </p>

  <h2>13. Contact Us</h2>
  <p>
    If you have questions or concerns about this Privacy Policy or your
    privacy rights, please contact us at
    <a href="mailto:support@spin-flip.com">support@spin-flip.com</a> or by
    mail at:
  </p>
  <p>
    SpinFlip LLC<br />
    Columbus, OH 43214<br />
    United States
  </p>
</div>

<style>
  .clear-nav {
    margin-top: 0;
    font-size: 1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }

  ul {
    margin-left: 1.5rem;
  }

  ul li {
    margin-bottom: 0.75rem;
  }

  h2 {
    margin-top: 2rem;
  }

  h3 {
    margin-top: 1.5rem;
  }

  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.9em;
  }

  @media (max-width: 768px) {
    .clear-nav {
      font-size: 0.9rem;
    }

    ul li {
      margin-bottom: 0.6rem;
    }
  }
</style>
